import { Circle as SvgCircle } from '@/components/common/charts/shapes/shapes';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    Circle,
    Divider,
    FlexProps,
    Square,
    SquareProps,
} from '@chakra-ui/react';
import styleguide from '@common/styleguide';
import { MouseEventHandler } from 'react';
import { linkHoverStyleProps } from '../styleHelpers';
import { Text } from '../Typography';
import { chartTheme } from './chartHelper';
import VerticalGridLine from './VerticalGridline';

export const defaultChartTooltipWidth = 200;
export const CHART_TOOLTIP_BASE_HEIGHT = 80;

interface ChartTooltipLineProps {
    visible: boolean;
    midpointX: number;
    midpointY: number;
    innerHeight: number;
    color?: string;
}

export function ChartTooltipLine({
    visible,
    midpointX,
    midpointY,
    innerHeight,
    color = styleguide.colors.white,
}: ChartTooltipLineProps) {
    if (!visible) return null;

    return (
        <>
            <VerticalGridLine x={midpointX} height={innerHeight} />
            <SvgCircle x={midpointX} y={midpointY} color={color} />
        </>
    );
}
interface ChartTooltipProps extends FlexProps {
    w?: number;
    title: string;
    headerRight?: JSX.Element;
    onMouseEnter?: MouseEventHandler<HTMLDivElement> | undefined;
    onMouseLeave?: MouseEventHandler<HTMLDivElement> | undefined;
}

export function ChartTooltip({
    w = defaultChartTooltipWidth,
    title,
    headerRight,
    children,
    ...props
}: ChartTooltipProps) {
    return (
        <FlexCol
            {...props}
            key={Math.random()}
            mt={1}
            position="absolute"
            w={w}
            color="text"
            rounded="md"
            shadow="md"
            bg="white"
            zIndex={chartTheme.zIndex.tooltip}
        >
            <FlexRow
                key="tooltip-header"
                py={4}
                px={3}
                bg="gray.100"
                roundedTop="md"
                justify="space-between"
                align="center"
            >
                <Text color="textDark" fontSize="sm" fontWeight="semibold">
                    {title}
                </Text>
                {headerRight}
            </FlexRow>
            <FlexCol key="tooltip-body" py={3} px={3}>
                {children}
            </FlexCol>
        </FlexCol>
    );
}

export type ChartTooltipLabelShape = 'diamond' | 'circle' | 'square';

export function ChartTooltipRow({
    label,
    labelColor,
    value,
    onClick,
    labelShape = 'circle',
    textColor = 'textDark',
    ...props
}: {
    label: string;
    labelColor?: SquareProps['bg'];
    labelShape?: ChartTooltipLabelShape;
    value: string;
    onClick?: VoidFunction;
    textColor?: string;
} & FlexProps) {
    return (
        <FlexRow
            justify="space-between"
            py={1}
            onClick={(e) => {
                if (!onClick) return;
                e.preventDefault();
                onClick();
            }}
            // If this item is clickable, add text hover effect; otherwise, don't.
            {...(onClick ? linkHoverStyleProps() : {})}
            {...props}
        >
            <FlexRow align="center">
                {labelColor && labelShape && (
                    <LabelShape color={labelColor} shape={labelShape} />
                )}
                <Text color={textColor} fontSize="sm">
                    {label}
                </Text>
            </FlexRow>
            <Text color={textColor} fontSize="sm">
                {value}
            </Text>
        </FlexRow>
    );
}

export const LabelShape = ({
    color,
    shape,
}: {
    color: SquareProps['bg'];
    shape: ChartTooltipLabelShape;
}) => (
    <>
        {shape === 'circle' && <Circle size={2} bg={color} mr={2} />}
        {shape === 'square' && <Square size={2} bg={color} mr={2} />}
        {shape === 'diamond' && (
            <Square
                size="6px"
                bg={color}
                mr={2}
                transform="rotateY(0deg) rotate(45deg)"
            />
        )}
    </>
);

export function ChartTooltipSummaryRow({
    label,
    value,
}: {
    label: string;
    value: string;
}) {
    return (
        <FlexCol pt={1}>
            <Divider />
            <FlexRow justify="space-between" pt={2}>
                <FlexRow align="center">
                    <Text
                        fontSize="xs"
                        letterSpacing="wide"
                        color="textGrayOnLight"
                    >
                        {label}
                    </Text>
                </FlexRow>
                <Text color="textDark" fontSize="sm" fontWeight="semibold">
                    {value}
                </Text>
            </FlexRow>
        </FlexCol>
    );
}
